









































import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { Storage } from "aws-amplify";
import Video from "@/components/files/details/Video.vue";
import Audio from "@/components/files/details/Audio.vue";
import { getFileType, getIcon } from "@/utils/FileUtils";
import { fileSaleSubscriptionService, SaleStatusUpdate, SaleStatusCallback } from "@/services/files/FileSaleSubscription";
import { FileSearchService } from "@/services/files/FileSearch";

@Component({
  components:{
    Video,
    Audio,
  }
})
export default class Preview extends Vue {
  @Prop()
  private file;

  private fileSearchService = new FileSearchService();
  private share = false;
  private sale = false;
  private caution = false;
  private protect = false;
  private rating = 0;
  private saleStatus = "";
  private saleStatusCallback: SaleStatusCallback | null = null;
  private currentRequestId: string | null = null;  // 現在監視中のrequestIdを保持
  private src: string | null = null;
  private poster: string | null = null;
  private audioIcon = "/img/audio.png";
  private videoIcon = "/img/video.png";
  private type = "";
  private imgClass = "grey darken";
  private imgMaxHeight = "400px";

  private get canRenderVideo(): boolean {
    return this.type === 'video' && this.src != null && this.poster != null;
  }

  private get canRenderAudio(): boolean {
    return this.type === 'audio' && this.src != null;
  }

  private get shouldShowMediaFallback(): boolean {
    if (this.type === 'video') {
      return !this.canRenderVideo;
    }

    if (this.type === 'audio') {
      return !this.canRenderAudio;
    }

    return false;
  }

  private async created(){
    await this.setFile();
  }

  @Watch('file')
  private async onFileChanged(){
    await this.setFile();
    // ファイル変更時はsubscriptionを解除（新しいファイルで外販送信するまで監視しない）
    this.stopWatchingSaleStatus();
  }

  private beforeDestroy() {
    // コールバックを解除
    this.stopWatchingSaleStatus();
  }

  /**
   * 外販送信ステータスの監視を開始
   * 外販送信実行時に呼び出される
   * @param requestId - 外販送信リクエストの一意ID
   */
  public startWatchingSaleStatus(requestId: string) {
    if (!this.file || !this.file.id) {
      return;
    }

    // 既存のrequestIdがある場合は先にクリーンアップ
    if (this.currentRequestId && this.currentRequestId !== requestId) {
      fileSaleSubscriptionService.unsubscribe(this.currentRequestId);
    }

    // 現在のrequestIdを保存
    this.currentRequestId = requestId;

    // コールバックを登録（自動的にsubscriptionも開始される）
    this.saleStatusCallback = async (data: SaleStatusUpdate) => {
      // 自分のファイルIDの更新のみ処理
      if (data.fileId === this.file.id) {
        // エラーステータスの場合はコールバックをリセット
        if (data.status === 'error') {
          this.saleStatusCallback = null;
          this.currentRequestId = null;
          return;
        }
        
        // 通常のステータス更新は再検索して最新情報を取得
        await this.refreshFileData(data.fileId);
      }
    };

    // ユーザー情報を取得
    const user = this.$store.getters.user;
    const accountId = user?.attributes?.['custom:account_id'] || '';
    const userId = user?.attributes?.sub || '';

    fileSaleSubscriptionService.subscribe(requestId, this.file.id, accountId, userId, this.saleStatusCallback);
  }

  /**
   * 最新のファイル情報を取得して更新
   * OpenSearchの同期を待つため、数秒待機してから実行
   */
  private async refreshFileData(fileId: string) {
    try {
      // OpenSearchの同期を待つため3秒待機
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const condition = {
        fileId: fileId
      };
      
      const result = await this.fileSearchService.searchFiles(condition, undefined, 0, 1);
      const items = result?.items;
      if (items && items.length > 0) {
        const updatedFile = items[0];
        
        // fileオブジェクトのsaleStatus関連プロパティを更新
        this.file.saleStatus = updatedFile.saleStatus;
        this.file.saleCount = updatedFile.saleCount;
        this.file.protect = updatedFile.protect;
        
        // 表示用のプロパティも更新
        this.saleStatus = updatedFile.saleStatus !== undefined && updatedFile.saleStatus !== null ? updatedFile.saleStatus : "";
        this.protect = updatedFile.protect && updatedFile.protect === 1 ? true : false;
        
        // 親コンポーネントに更新を通知
        this.$emit('file-updated', {
          fileId: fileId,
          saleStatus: updatedFile.saleStatus,
          saleCount: updatedFile.saleCount,
          protect: updatedFile.protect
        });
        
        // 成功/失敗時はsubscriptionが自動停止されるため、コールバックをリセット
        if (updatedFile.saleStatus === 'success' || updatedFile.saleStatus === 'failure') {
          this.saleStatusCallback = null;
          this.currentRequestId = null;
        }
      } else {
        console.warn(`File not found in OpenSearch (fileId: ${fileId})`);
        this.stopWatchingSaleStatus();
      }
    } catch (error) {
      console.error('Failed to refresh file from OpenSearch:', error);
      // OpenSearch取得エラー時はsubscriptionを停止
      this.stopWatchingSaleStatus();
    }
  }

  /**
   * 外販送信ステータスの監視を停止
   */
  public stopWatchingSaleStatus() {
    if (this.currentRequestId) {
      fileSaleSubscriptionService.unsubscribe(this.currentRequestId);
    }
    this.saleStatusCallback = null;
    this.currentRequestId = null;
  }

  private async setFile(){
    this.src = null;
    this.poster = null;
    this.type = "";
    if(this.file){
      const customItems = this.file.customItems ? JSON.parse(this.file.customItems) : undefined;
      this.share = customItems && customItems.share === 0 ? true : false;
      this.sale = customItems && customItems.sale === 0 ? true : false;
      this.caution = this.file.caution && this.file.caution === 1 ? true : false;
      this.protect = this.file.protect && this.file.protect === 1 ? true : false;
      this.rating = this.file.rating ? this.file.rating : 0;
      this.saleStatus = this.file.saleStatus !== undefined ? this.file.saleStatus : "";
      this.type = this.file.type && this.file.extension ? getFileType(this.file.extension, this.file.type) : "";
      const meta = this.file.meta ? JSON.parse(this.file.meta) : undefined;
      // TODO:S3のURIかS3のキーかは要検討
      const thumbnailUri = meta && meta.thumbnailUri ? meta.thumbnailUri.split('/') : [];
      const previewUri = meta && meta.previewUri ? meta.previewUri.split('/') : [];      

      switch(this.type){
        case "image": {
          if(thumbnailUri.length > 0){
            this.src = (await Storage.get(`${thumbnailUri[4]}/${thumbnailUri[5]}/${thumbnailUri[6]}/${thumbnailUri[7]}`, {download: false, expires: 300, contentType: 'image/jpeg'})).toString();
          }
          break;
        }
        case "video": {
          if(thumbnailUri.length > 0 && previewUri.length > 0){
            this.src = (await Storage.get(`${previewUri[4]}/${previewUri[5]}/${previewUri[6]}/${previewUri[7]}`, {download: false, expires: 300, contentType: 'video/mp4'})).toString(); 
            this.poster = (await Storage.get(`${thumbnailUri[4]}/${thumbnailUri[5]}/${thumbnailUri[6]}/${thumbnailUri[7]}`, {download: false, expires: 300, contentType: 'image/jpeg'})).toString();
          }
          break;
        }
        case "audio": {
          if(previewUri.length > 0){
            this.src = (await Storage.get(`${previewUri[4]}/${previewUri[5]}/${previewUri[6]}/${previewUri[7]}`, {download: false, expires: 300, contentType: 'audio/mpeg'})).toString();
          }
          break;
        }
        default : {
          this.src = getIcon(this.type);
          break;
        }
      }

      if(this.type !== 'image' && (this.type !== 'video' && this.src !== null && this.poster !== null)){
        this.imgMaxHeight = "200px";
        this.imgClass = "";
      }else{
        this.imgClass = "grey darken";
        this.imgMaxHeight = "400px";
      }
    }

  }
}
